import { useState, useMemo, Fragment } from 'react';
import { useQuery, useMutation, useLazyQuery } from '@apollo/client';
import {
  FaPlus,
  FaSearch,
  FaShoppingBag,
  FaChevronDown,
  FaChevronUp,
  FaChevronLeft,
  FaChevronRight,
  FaCheck,
  FaBoxOpen,
  FaBan,
  FaEye
} from 'react-icons/fa';
import toast from 'react-hot-toast';
import CompraFormModal from '../components/egresos/CompraFormModal';
import RecibirCompraModal from '../components/egresos/RecibirCompraModal';
import ConfirmModal from '../components/shared/ConfirmModal';
import InputModal from '../components/shared/InputModal';
import { GET_COMPRAS, GET_COMPRA, CONFIRMAR_COMPRA, ANULAR_COMPRA } from '../graphql/compras';
import { GET_PROVEEDORES } from '../graphql/proveedores';
import './InventarioPage.css';
import './Compras.css';

/*
  Usage:
  <Route path="/compras" element={<PrivateRoute><Compras /></PrivateRoute>} />
*/

const formatCurrency = (value) =>
  new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0
  }).format(value || 0);

const formatFecha = (fecha) => {
  if (!fecha) return '—';
  return new Date(fecha).toLocaleDateString('es-CO', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

function getBaseUrl() {
  const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4005/graphql';
  return apiUrl.replace(/\/graphql$/, '');
}

const LIMIT = 20;

const ESTADO_CONFIG = {
  borrador: { label: 'Borrador', color: '#6b7280', bg: '#f3f4f6' },
  confirmada: { label: 'Confirmada', color: '#3b82f6', bg: '#eff6ff' },
  recibida_parcial: { label: 'Recibida Parcial', color: '#f59e0b', bg: '#fffbeb' },
  recibida: { label: 'Recibida', color: '#10b981', bg: '#d1fae5' },
  anulada: { label: 'Anulada', color: '#ef4444', bg: '#fee2e2' }
};

const ESTADO_PAGO_CONFIG = {
  pendiente: { label: 'Pendiente', color: '#f59e0b', bg: '#fffbeb' },
  pagado: { label: 'Pagado', color: '#10b981', bg: '#d1fae5' },
  parcial: { label: 'Parcial', color: '#3b82f6', bg: '#eff6ff' }
};

const EstadoBadge = ({ estado, config }) => {
  const cfg = config[estado] || { label: estado, color: '#6b7280', bg: '#f3f4f6' };
  return (
    <span
      className="compra-estado-badge"
      style={{ color: cfg.color, backgroundColor: cfg.bg }}
    >
      {cfg.label}
    </span>
  );
};

const Compras = () => {
  const [proveedorFiltro, setProveedorFiltro] = useState('');
  const [estadoFiltro, setEstadoFiltro] = useState('');
  const [fechaDesde, setFechaDesde] = useState('');
  const [fechaHasta, setFechaHasta] = useState('');
  const [offset, setOffset] = useState(0);
  const [filaExpandida, setFilaExpandida] = useState(null);
  const [mostrarCompraModal, setMostrarCompraModal] = useState(false);
  const [mostrarRecibirModal, setMostrarRecibirModal] = useState(false);
  const [compraParaRecibir, setCompraParaRecibir] = useState(null);
  const [mostrarConfirmarModal, setMostrarConfirmarModal] = useState(false);
  const [mostrarAnularModal, setMostrarAnularModal] = useState(false);
  const [compraAccion, setCompraAccion] = useState(null);
  const [vistaPrevia, setVistaPrevia] = useState(null);

  const { data, loading, refetch } = useQuery(GET_COMPRAS, {
    variables: {
      proveedor_id: proveedorFiltro ? parseInt(proveedorFiltro) : null,
      estado: estadoFiltro || null,
      fecha_desde: fechaDesde || null,
      fecha_hasta: fechaHasta || null,
      limit: LIMIT,
      offset
    },
    fetchPolicy: 'network-only'
  });

  const { data: dataProveedores } = useQuery(GET_PROVEEDORES, {
    variables: { activo: true }
  });

  const [fetchCompra] = useLazyQuery(GET_COMPRA, {
    fetchPolicy: 'network-only',
    onCompleted: (data) => {
      if (data?.compra) {
        setCompraParaRecibir(data.compra);
        setMostrarRecibirModal(true);
      }
    },
    onError: (err) => toast.error(err.message)
  });

  const [confirmarCompra] = useMutation(CONFIRMAR_COMPRA, {
    onCompleted: () => {
      toast.success('Compra confirmada correctamente');
      refetch();
    },
    onError: (err) => toast.error(err.message)
  });

  const [anularCompra] = useMutation(ANULAR_COMPRA, {
    onCompleted: () => {
      toast.success('Compra anulada');
      refetch();
    },
    onError: (err) => toast.error(err.message)
  });

  const compras = data?.compras?.compras || [];
  const total = data?.compras?.total || 0;
  const proveedores = dataProveedores?.proveedores || [];

  const estadisticas = useMemo(() => {
    const totalCompras = compras.length;
    const pendientesRecepcion = compras.filter(
      (c) => c.estado === 'confirmada' || c.estado === 'recibida_parcial'
    ).length;
    const recibidas = compras.filter((c) => c.estado === 'recibida').length;
    const totalInvertido = compras
      .filter((c) => c.estado !== 'anulada')
      .reduce((acc, c) => acc + (c.total || 0), 0);
    return { totalCompras, pendientesRecepcion, recibidas, totalInvertido };
  }, [compras]);

  const totalPaginas = Math.ceil(total / LIMIT);
  const paginaActual = Math.floor(offset / LIMIT) + 1;

  const handleConfirmar = (compra) => {
    setCompraAccion(compra);
    setMostrarConfirmarModal(true);
  };

  const ejecutarConfirmar = () => {
    if (!compraAccion) return;
    confirmarCompra({ variables: { id: parseInt(compraAccion.id) } });
    setMostrarConfirmarModal(false);
    setCompraAccion(null);
  };

  const handleRecibir = (compra) => {
    fetchCompra({ variables: { id: parseInt(compra.id) } });
  };

  const handleAnular = (compra) => {
    setCompraAccion(compra);
    setMostrarAnularModal(true);
  };

  const ejecutarAnular = (motivo) => {
    if (!compraAccion) return;
    anularCompra({ variables: { id: parseInt(compraAccion.id), motivo: motivo || null } });
    setMostrarAnularModal(false);
    setCompraAccion(null);
  };

  const handleToggleExpansion = (id) => {
    setFilaExpandida(filaExpandida === id ? null : id);
  };

  const handleCerrarCompraModal = () => {
    setMostrarCompraModal(false);
    refetch();
  };

  const handleCerrarRecibirModal = () => {
    setMostrarRecibirModal(false);
    setCompraParaRecibir(null);
    refetch();
  };

  const handleFiltroChange = () => setOffset(0);

  return (
      <div className="inventario-page">
        {/* Header */}
        <div className="inventario-header">
          <div className="header-titulo">
            <FaShoppingBag className="header-icon" />
            <div>
              <h1>Compras</h1>
              <p>Gestion de ordenes de compra a proveedores</p>
            </div>
          </div>

          <div className="header-actions">
            <button className="btn-primary" onClick={() => setMostrarCompraModal(true)}>
              <FaPlus /> Nueva Compra
            </button>
          </div>
        </div>

        {/* Estadisticas */}
        <div className="inventario-stats">
          <div className="stat-card">
            <div className="stat-value">{estadisticas.totalCompras}</div>
            <div className="stat-label">Total Compras</div>
          </div>
          <div className={`stat-card ${estadisticas.pendientesRecepcion > 0 ? 'stat-warning' : ''}`}>
            <div className="stat-value">{estadisticas.pendientesRecepcion}</div>
            <div className="stat-label">Pendientes Recepcion</div>
          </div>
          <div className="stat-card">
            <div className="stat-value">{estadisticas.recibidas}</div>
            <div className="stat-label">Recibidas</div>
          </div>
          <div className="stat-card">
            <div className="stat-value compra-stat-currency">{formatCurrency(estadisticas.totalInvertido)}</div>
            <div className="stat-label">Total Invertido</div>
          </div>
        </div>

        {/* Filtros */}
        <div className="inventario-filtros">
          <select
            className="filtro-select"
            value={proveedorFiltro}
            onChange={(e) => {
              setProveedorFiltro(e.target.value);
              handleFiltroChange();
            }}
          >
            <option value="">Todos los proveedores</option>
            {proveedores.map((prov) => (
              <option key={prov.id} value={prov.id}>
                {prov.nombre}
              </option>
            ))}
          </select>

          <select
            className="filtro-select"
            value={estadoFiltro}
            onChange={(e) => {
              setEstadoFiltro(e.target.value);
              handleFiltroChange();
            }}
          >
            <option value="">Todos los estados</option>
            {Object.entries(ESTADO_CONFIG).map(([key, cfg]) => (
              <option key={key} value={key}>
                {cfg.label}
              </option>
            ))}
          </select>

          <div className="gasto-filtro-fechas">
            <label className="gasto-fecha-label">Desde</label>
            <input
              type="date"
              className="filtro-fecha"
              value={fechaDesde}
              onChange={(e) => {
                setFechaDesde(e.target.value);
                handleFiltroChange();
              }}
            />
          </div>

          <div className="gasto-filtro-fechas">
            <label className="gasto-fecha-label">Hasta</label>
            <input
              type="date"
              className="filtro-fecha"
              value={fechaHasta}
              onChange={(e) => {
                setFechaHasta(e.target.value);
                handleFiltroChange();
              }}
            />
          </div>
        </div>

        {/* Tabla */}
        <div className="inventario-tabla-container">
          {loading ? (
            <div className="loading-state">Cargando compras...</div>
          ) : compras.length === 0 ? (
            <div className="empty-state">
              <FaShoppingBag />
              <p>No hay compras para mostrar</p>
              <button className="btn-primary" onClick={() => setMostrarCompraModal(true)}>
                <FaPlus /> Crear primera compra
              </button>
            </div>
          ) : (
            <table className="inventario-tabla compra-tabla">
              <thead>
                <tr>
                  <th className="col-expand"></th>
                  <th>Codigo</th>
                  <th>Fecha</th>
                  <th>Proveedor</th>
                  <th>Items</th>
                  <th>Total</th>
                  <th>Estado</th>
                  <th>Estado Pago</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {compras.map((compra) => (
                  <Fragment key={compra.id}>
                    <tr
                      className={`compra-row ${filaExpandida === compra.id ? 'compra-row-expandida' : ''}`}
                    >
                      <td className="col-expand">
                        <button
                          className="btn-expand"
                          onClick={() => handleToggleExpansion(compra.id)}
                          title={filaExpandida === compra.id ? 'Colapsar' : 'Ver detalles'}
                        >
                          {filaExpandida === compra.id ? <FaChevronUp /> : <FaChevronDown />}
                        </button>
                      </td>
                      <td>
                        <span className="codigo">{compra.codigo}</span>
                      </td>
                      <td>{formatFecha(compra.fecha)}</td>
                      <td>
                        <div className="item-nombre">
                          <strong>{compra.proveedor?.nombre || '—'}</strong>
                          {compra.numero_factura_proveedor && (
                            <small>Fact. {compra.numero_factura_proveedor}</small>
                          )}
                        </div>
                      </td>
                      <td>
                        <span className="compra-items-count">
                          {compra.detalles?.length ?? '—'} items
                        </span>
                      </td>
                      <td>
                        <strong>{formatCurrency(compra.total)}</strong>
                      </td>
                      <td>
                        <EstadoBadge estado={compra.estado} config={ESTADO_CONFIG} />
                      </td>
                      <td>
                        <EstadoBadge estado={compra.estado_pago} config={ESTADO_PAGO_CONFIG} />
                      </td>
                      <td>
                        <div className="acciones compra-acciones">
                          {compra.comprobante_url && (
                            <button
                              className="btn-accion btn-ver-adjunto"
                              onClick={() => setVistaPrevia(compra.comprobante_url)}
                              title="Ver archivo adjunto"
                            >
                              <FaEye />
                            </button>
                          )}

                          {compra.estado === 'borrador' && (
                            <button
                              className="btn-accion btn-confirmar"
                              onClick={() => handleConfirmar(compra)}
                              title="Confirmar compra"
                            >
                              <FaCheck />
                            </button>
                          )}

                          {(compra.estado === 'confirmada' || compra.estado === 'recibida_parcial') && (
                            <button
                              className="btn-accion btn-recibir"
                              onClick={() => handleRecibir(compra)}
                              title="Recibir mercancia"
                            >
                              <FaBoxOpen />
                            </button>
                          )}

                          {compra.estado !== 'anulada' && compra.estado !== 'recibida' && (
                            <button
                              className="btn-accion btn-anular"
                              onClick={() => handleAnular(compra)}
                              title="Anular compra"
                            >
                              <FaBan />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {filaExpandida === compra.id && (
                      <tr key={`${compra.id}-detalle`} className="compra-detalle-row">
                        <td colSpan={9}>
                          <div className="compra-detalle-container">
                            <h4 className="compra-detalle-titulo">Detalle de items</h4>
                            {!compra.detalles || compra.detalles.length === 0 ? (
                              <p className="compra-sin-detalles">Sin items registrados</p>
                            ) : (
                              <table className="compra-detalle-tabla">
                                <thead>
                                  <tr>
                                    <th>Item</th>
                                    <th>Cantidad</th>
                                    <th>Recibido</th>
                                    <th>Precio Unit.</th>
                                    <th>Subtotal</th>
                                    <th>Total</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {compra.detalles.map((det) => (
                                    <tr key={det.id}>
                                      <td>
                                        <div className="item-nombre">
                                          <strong>{det.nombre_item}</strong>
                                          {det.codigo_item && <small>{det.codigo_item}</small>}
                                        </div>
                                      </td>
                                      <td>{det.cantidad}</td>
                                      <td>
                                        <span
                                          className={
                                            det.cantidad_recibida >= det.cantidad
                                              ? 'recibido-completo'
                                              : det.cantidad_recibida > 0
                                              ? 'recibido-parcial'
                                              : 'recibido-pendiente'
                                          }
                                        >
                                          {det.cantidad_recibida} / {det.cantidad}
                                        </span>
                                      </td>
                                      <td>{formatCurrency(det.precio_unitario)}</td>
                                      <td>{formatCurrency(det.subtotal)}</td>
                                      <td>
                                        <strong>{formatCurrency(det.total)}</strong>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Paginacion */}
        {total > LIMIT && (
          <div className="gasto-paginacion">
            <span className="paginacion-info">
              Mostrando {offset + 1}–{Math.min(offset + LIMIT, total)} de {total} compras
            </span>
            <div className="paginacion-controles">
              <button
                className="btn-paginacion"
                onClick={() => setOffset(offset - LIMIT)}
                disabled={offset === 0}
              >
                <FaChevronLeft />
              </button>
              <span className="paginacion-pagina">
                {paginaActual} / {totalPaginas}
              </span>
              <button
                className="btn-paginacion"
                onClick={() => setOffset(offset + LIMIT)}
                disabled={offset + LIMIT >= total}
              >
                <FaChevronRight />
              </button>
            </div>
          </div>
        )}

        {/* Modales */}
        {mostrarCompraModal && (
          <CompraFormModal
            isOpen={mostrarCompraModal}
            onClose={handleCerrarCompraModal}
          />
        )}

        {mostrarRecibirModal && compraParaRecibir && (
          <RecibirCompraModal
            isOpen={mostrarRecibirModal}
            onClose={handleCerrarRecibirModal}
            compra={compraParaRecibir}
          />
        )}

        <ConfirmModal
          isOpen={mostrarConfirmarModal}
          onClose={() => { setMostrarConfirmarModal(false); setCompraAccion(null); }}
          onConfirm={ejecutarConfirmar}
          title="Confirmar Orden de Compra"
          message={`¿Confirmar la orden ${compraAccion?.codigo}? Una vez confirmada, estara lista para recepcion de mercancia.`}
          variant="info"
          confirmText="Confirmar Compra"
        />

        <InputModal
          isOpen={mostrarAnularModal}
          onClose={() => { setMostrarAnularModal(false); setCompraAccion(null); }}
          onConfirm={ejecutarAnular}
          title="Anular Compra"
          message={`¿Anular la compra ${compraAccion?.codigo}? Si ya fue recibida, el stock sera revertido.`}
          placeholder="Motivo de la anulacion (opcional)..."
          confirmText="Anular Compra"
        />

        {/* Visor de comprobante */}
        {vistaPrevia && (() => {
          const baseUrl = getBaseUrl();
          const fullUrl = vistaPrevia.startsWith('http') ? vistaPrevia : `${baseUrl}${vistaPrevia}`;
          const esPdf = vistaPrevia.toLowerCase().endsWith('.pdf');
          return (
            <div className="comprobante-lightbox" onClick={() => setVistaPrevia(null)}>
              <div className="comprobante-lightbox__content" onClick={(e) => e.stopPropagation()}>
                <button className="comprobante-lightbox__close" onClick={() => setVistaPrevia(null)}>✕</button>
                {esPdf ? (
                  <iframe src={fullUrl} className="comprobante-lightbox__iframe" title="Comprobante PDF" />
                ) : (
                  <img src={fullUrl} alt="Comprobante" className="comprobante-lightbox__img" />
                )}
              </div>
            </div>
          );
        })()}
      </div>
  );
};

export default Compras;
